// 📁 config/billingCatalogLabels.js
//
// Central map from stable backend catalog CODES to customer-facing display labels.
//
// RULE: customer-facing UI must NEVER show raw catalog codes (CLOUD_M, ROOMS, CLOUD_KEY, …) as
// the primary label. Always render through these maps. Raw codes may appear only in internal
// admin / debug contexts, as secondary metadata.

// Plans — commercial names. `eyebrow` sits above the name ("Zezamii Cloud"), `name` is the tier.
export const PLAN_DISPLAY = {
	CLOUD_S: { name: 'Starter', eyebrow: 'Zezamii Cloud', description: 'Core cloud access for smaller sites.' },
	CLOUD_M: {
		name: 'Growth',
		eyebrow: 'Zezamii Cloud',
		description: 'More capacity and product coverage for growing teams.'
	},
	CLOUD_L: {
		name: 'Business',
		eyebrow: 'Zezamii Cloud',
		description: 'Advanced access, integrations, and administration.'
	},
	CLOUD_E: {
		name: 'Enterprise',
		eyebrow: 'Zezamii Cloud',
		description: 'Custom terms, integrations, support, and governance.'
	},
	// Estate — the fifth tier (backend CLOUD_X, seeded by App Server migration 010).
	// ⚠️ The code is CLOUD_X, NOT CLOUD_XL. CLOUD_XL is this file's test fixture for the
	// humanize fallback (billingCatalogLabels.test.js) and must stay an unknown code —
	// defining it here would quietly invert what that test asserts.
	CLOUD_X: {
		name: 'Estate',
		eyebrow: 'Zezamii Cloud',
		description: 'Portfolio-scale access across many sites, with channel and governance controls.'
	}
};

// Modules / features / packs.
//
// NOTE on CAMERAS → "Vision": the customer-facing module name is now "Vision" (packaged under
// Access). This is a PRESENTATION relabel only — the live entitlement/operational code stays
// `CAMERAS` (and `/v5/cameras` is untouched). The catalog-corrections backend track introduces a
// real `VISION` code later; both map to the same label here so the swap is label-stable.
export const CATALOG_DISPLAY = {
	ACCESS: 'Access',
	ROOMS: 'Rooms',
	BOOKINGS: 'Bookings',
	LOCKERS: 'Lockers',
	CAMERAS: 'Vision', // relabel only — code remains CAMERAS until the backend migration
	VISION: 'Vision',
	VISION_AI: 'Vision AI',
	CLOUD_KEY: 'Cloud Key',
	ACCESS_SCIM: 'SCIM',
	ADVANCED_REPORTING: 'Advanced Reporting',
	API_ACCESS: 'API Access',
	DASHBOARD_EXPORTS: 'Dashboard Exports',
	NOTIFICATIONS: 'Notifications',
	VERKADA: 'Verkada',
	VORTEX: 'Vortex',
	// SMS is a platform usage allowance; CLOUD_KEY_SMS is the legacy metered code (kept working).
	CLOUD_KEY_SMS: 'SMS allowance',
	PLATFORM_SMS: 'SMS allowance',
	SMS_ALLOWANCE: 'SMS allowance',
	SMS_PACK_1000: '1,000 SMS pack',
	SMS_PACK_5000: '5,000 SMS pack',
	SMS_PACK_10000: '10,000 SMS pack',
	SUPPORT_PRIORITY: 'Priority support',
	SUPPORT_PREMIUM: 'Premium support',
	SUPPORT_ENTERPRISE: 'Enterprise support'
};

// Features that are PACKAGED UNDER a parent module (mirrors catalog ParentCode). These render
// nested beneath their module on the Billing page — never as top-level lines. Vision (CAMERAS),
// Cloud Key and SCIM all sit under Access.
export const FEATURES_BY_MODULE = {
	ACCESS: ['CLOUD_KEY', 'ACCESS_SCIM', 'CAMERAS'] // CAMERAS displays as "Vision"
};

// The top-level sellable modules shown as Subscription lines on the Billing page (order matters).
// Features (Cloud Key / SCIM / Vision) are intentionally NOT here — they nest under Access via
// FEATURES_BY_MODULE.
export const BILLING_MODULE_CODES = ['ACCESS', 'ROOMS', 'BOOKINGS', 'LOCKERS'];

// Everything an internal admin can grant as an entitlement: the top-level modules PLUS the nested
// features (so Cloud Key / SCIM / Vision stay grantable from the admin panel even though they are
// no longer top-level billing tiles).
export const BILLING_GRANTABLE_CODES = [...BILLING_MODULE_CODES, ...Object.values(FEATURES_BY_MODULE).flat()];

// The subscribable plan tiers (order = display order). Single source for plan pickers so the
// Stripe subscribe form and the internal admin panel can't fall out of sync with the catalog.
export const BILLING_PLAN_CODES = ['CLOUD_S', 'CLOUD_M', 'CLOUD_L', 'CLOUD_E', 'CLOUD_X'];

// Humanize an unknown code as a last resort — Title Case, underscores → spaces — so we still never
// surface a raw SCREAMING_SNAKE code to a customer.
const humanize = (code) =>
	String(code || '')
		.toLowerCase()
		.split('_')
		.filter(Boolean)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(' ');

/**
 * Plan display metadata for a plan code.
 * @returns {{ name:string, eyebrow:string, description:string, full:string, code:string }}
 *   `full` is the full marketing name ("Zezamii Cloud Growth"); `code` is the raw code (admin only).
 */
export const planLabel = (code) => {
	const meta = PLAN_DISPLAY[code];
	if (meta) return { ...meta, full: `${meta.eyebrow} ${meta.name}`, code };
	const name = humanize(code) || 'Plan';
	return { name, eyebrow: 'Zezamii Cloud', description: '', full: `Zezamii Cloud ${name}`, code };
};

/** Customer-facing label for a module/feature/pack code. */
export const moduleLabel = (code) => CATALOG_DISPLAY[code] || humanize(code) || code;

const billingCatalogLabels = {
	PLAN_DISPLAY,
	CATALOG_DISPLAY,
	FEATURES_BY_MODULE,
	BILLING_MODULE_CODES,
	BILLING_GRANTABLE_CODES,
	BILLING_PLAN_CODES,
	planLabel,
	moduleLabel
};
export default billingCatalogLabels;
